import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { Users } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { useCurrentOrg } from '@/hooks/use-current-org';
import { useOrgContext } from '@/hooks/use-org-context';
import { API_URL, fetchWithTimeout } from '@/lib/api/core';
import { loginInvitationCopy, publicJoinSignInSearch } from '@/lib/sign-in-redirect';
import { shouldRenderPublicOrgJoinBar } from './public-org-join-bar.visibility';

export function PublicOrgJoinBar() {
  const { session, urlOrgSlug, authReady } = useOrgContext();
  const { org, isMember, isPublic, isLoading } = useCurrentOrg();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [isJoining, setIsJoining] = useState(false);

  if (
    !shouldRenderPublicOrgJoinBar({
      authReady,
      org,
      urlOrgSlug,
      isPublic,
      isMember,
      session,
      isLoading,
    }) ||
    !org ||
    !urlOrgSlug
  ) {
    return null;
  }

  const handleJoin = async () => {
    if (!session) {
      navigate({
        to: '/auth/login',
        search: publicJoinSignInSearch(org.name),
      });
      return;
    }

    setIsJoining(true);
    try {
      const response = await fetchWithTimeout(`${API_URL}/api/${urlOrgSlug}/join`, {
        method: 'POST',
        credentials: 'include',
      });
      if (!response.ok) {
        const body = await response.text();
        throw new Error(body || `Failed to join (${response.status})`);
      }
      toast.success(`Joined ${org.name}`);
      await queryClient.invalidateQueries({ queryKey: ['organizations'] });
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to join workspace');
    } finally {
      setIsJoining(false);
    }
  };

  return (
    <div className="sticky top-0 z-30 border-b border-border bg-primary/5 backdrop-blur-sm">
      <div className="mx-auto flex max-w-screen-2xl items-center gap-3 px-4 py-2 lg:px-6">
        <Users className="h-4 w-4 shrink-0 text-primary" />
        <p className="flex-1 text-sm">
          You're browsing <span className="font-medium">{org.name}</span> as a visitor.
          {session ? ' Join to contribute and see full workspace details.' : null}
        </p>
        <Button size="sm" onClick={handleJoin} disabled={isJoining}>
          {isJoining
            ? 'Joining…'
            : session
              ? `Join ${org.name}`
              : loginInvitationCopy(org.name, undefined)?.title}
        </Button>
      </div>
    </div>
  );
}
